// src/components/pages/simulation/utils/MonteCarloUtils.js

export const WORKER_URL = '/worker/mc_worker.js'; // 경로 확인 필요 (public/workers/..)
export const NUM_WORKERS = Math.min(4, navigator.hardwareConcurrency || 2);

export function getCurrencySymbol(currency) {
  if (currency === "USD") return "$";
  if (currency === "EUR") return "€";
  return "원";
}

export function getFormattedDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export function getDefaultDates() {
    const today = new Date();
    const thirtyYearsAgo = new Date();
    thirtyYearsAgo.setFullYear(today.getFullYear() - 30);
    return {
        today: getFormattedDate(today),
        thirtyYearsAgo: getFormattedDate(thirtyYearsAgo)
    };
}

// 시뮬레이션 결과 분석 (통계 처리)
export function analyzeSimulationResults(finalValues, allPaths, maxDrawdowns, simYears, simCount, targetAmount, initialInvestment, monthlyContribution, contributionIncrease, isWithdrawalMode, minSurvivalYears = simYears ) {
    
    // 1. 생존 확률 (잔액 > 0)
    let survivalCount = finalValues.filter(v => v > 0.01).length; 
    let survivalRate = (survivalCount / simCount) * 100;

    // 2. 백분위수 계산 (Final Value)
    const sortedFinalValues = [...finalValues].sort((a, b) => a - b);
    const percentiles = [10, 25, 50, 75, 90];
    const finalValuePercentiles = {};
    
    percentiles.forEach(p => {
        const index = Math.floor(simCount * (p / 100)); 
        const safeIndex = Math.min(index, sortedFinalValues.length - 1);
        finalValuePercentiles[`p${p}`] = sortedFinalValues[safeIndex];
    });

    const medianFinalValue = finalValuePercentiles['p50'];

    // 3. 백분위수 경로 추출 (차트용)
    const percentilePaths = [90, 75, 50, 25, 10].map(p => {
        const index = Math.floor(simCount * (p / 100));
        const percentilePath = [];
        for (let y = 0; y <= simYears; y++) {
             const yearValues = allPaths.map(path => path[y]).sort((a, b) => a - b);
             const safeIndex = Math.min(index, yearValues.length - 1);
             percentilePath.push(yearValues[safeIndex]);
        }
        return { percentile: p, path: percentilePath };
    });

    // 4. MDD 분석
    const sortedMaxDrawdowns = [...maxDrawdowns].sort((a, b) => a - b);
    const mddPercentiles = {};
    percentiles.forEach(p => {
        const index = Math.floor(simCount * (p / 100)); 
        const safeIndex = Math.min(index, sortedMaxDrawdowns.length - 1);
        mddPercentiles[`p${p}`] = sortedMaxDrawdowns[safeIndex];
    });

    // 5. 목표 및 성공 지표
    let goalMetrics = { successRate: 0, medianYears: null, label: '' };
    let medianRuinYear = simYears; 
    let p10RuinYear = simYears; // [추가] 하위 10% 운일 때의 고갈 시점

    if (isWithdrawalMode) {
        // [수정 2] 모든 시나리오의 고갈 시점을 기록
        const allRuinYears = allPaths.map(path => {
            const idx = path.findIndex(v => v <= 0);
            return idx === -1 ? Infinity : idx; 
        });

        // [핵심 추가] minSurvivalYears 이상 생존한 시나리오 개수로 생존 확률 재계산!
        const survivedTargetCount = allRuinYears.filter(year => year > minSurvivalYears).length;
        survivalRate = (survivedTargetCount / simCount) * 100; // 덮어쓰기
        
        goalMetrics.successRate = survivalRate;
        goalMetrics.label = "Survival Probability";

        // 빨리 고갈된 순서부터 늦게 고갈(혹은 생존)된 순으로 줄 세우기
        allRuinYears.sort((a, b) => a - b);

        // 전체 기준 하위 10%(P10)와 중앙값(P50) 추출
        const p10Idx = Math.floor(simCount * 0.10);
        const p50Idx = Math.floor(simCount * 0.50);

        // p10RuinYear = allRuinYears[p10Idx] === Infinity ? simYears : allRuinYears[p10Idx];
        // medianRuinYear = allRuinYears[p50Idx] === Infinity ? simYears : allRuinYears[p50Idx];
        p10RuinYear = allRuinYears[p10Idx]; // Infinity 유지
        medianRuinYear = allRuinYears[p50Idx];

        goalMetrics.medianYears = medianRuinYear;
    }
    else {
        if (targetAmount > 0) {
            const successPaths = allPaths.filter(path => path[path.length - 1] >= targetAmount);
            goalMetrics.successRate = (successPaths.length / allPaths.length) * 100;
            goalMetrics.label = "Goal Probability";

            const reachingYears = allPaths.map(path => {
                const yearIndex = path.findIndex(val => val >= targetAmount);
                return yearIndex !== -1 ? yearIndex : null;
            }).filter(y => y !== null);

            let medianYears = null;
            if (reachingYears.length > 0) {
                reachingYears.sort((a, b) => a - b);
                medianYears = reachingYears[Math.floor(reachingYears.length / 2)];
            }
            goalMetrics.medianYears = medianYears;
        }
    }

    // 6. 누적 원금 경로 계산 (Principal Path)
    const principalPath = [initialInvestment];
    let currentAnnualContribution = monthlyContribution * 12;
    let cumulativePrincipal = initialInvestment;

    for (let y = 1; y <= simYears; y++) {
        if (!isWithdrawalMode) {
            cumulativePrincipal += currentAnnualContribution;
            currentAnnualContribution *= (1 + contributionIncrease / 100);
        }
        principalPath.push(cumulativePrincipal);
    }

    // 7. 마일스톤 데이터 추출
    const milestones = [];
    const interval = 5;
    const getPValueAtYear = (p, year) => {
        const pathObj = percentilePaths.find(obj => obj.percentile === p);
        return pathObj ? pathObj.path[year] : 0;
    };

    for (let y = interval; y <= simYears; y += interval) {
        milestones.push({
            year: y,
            p10: getPValueAtYear(10, y),
            p50: getPValueAtYear(50, y),
            p90: getPValueAtYear(90, y),
            principal: principalPath[y]
        });
    }

    if (simYears % interval !== 0) {
        milestones.push({
            year: simYears,
            p10: getPValueAtYear(10, simYears),
            p50: getPValueAtYear(50, simYears),
            p90: getPValueAtYear(90, simYears),
            principal: principalPath[simYears]
        });
    }

    // [NEW] 8. 애니메이션을 위한 연도별 분포 및 연도별 누적 MDD 데이터 추출
    const allPathsAtYear = {};
    const allMddAtYear = {};

    for (let y = 0; y <= simYears; y++) {
        allPathsAtYear[y] = new Array(simCount);
        allMddAtYear[y] = new Array(simCount);
    }

    // 성능 최적화를 위해 경로별로 한 번에 순회하여 연도별 MDD와 자산을 할당합니다.
    for (let p = 0; p < allPaths.length; p++) {
        const path = allPaths[p];
        let maxD = 0;
        let peak = -Infinity;

        for (let y = 0; y <= simYears; y++) {
            const val = path[y];
            allPathsAtYear[y][p] = val;

            if (val > peak) peak = val;
            const dd = peak === 0 ? 0 : (peak - val) / peak;
            if (dd > maxD) maxD = dd;
            
            allMddAtYear[y][p] = maxD * 100; // %로 변환
        }
    }

    return {
        survivalRate,
        medianFinalValue,
        medianRuinYear,
        percentilePaths,
        mddPercentiles,
        p10RuinYear,
        finalValuePercentiles,
        finalValues, 
        maxDrawdowns, 
        goalMetrics,
        backgroundPaths: allPaths.slice(0, 1000),
        principalPath, 
        milestones,
        allPathsAtYear,
        allMddAtYear // [NEW] 반환 객체에 추가
    };
}

// Web Worker 실행 함수 
export function runSimulationInWorkers(stats, simYears, simCount, initialInvestment, targetAmount, monthlyContribution, contributionIncrease, isWithdrawalMode, withdrawalRate, inflationRate, minSurvivalYears) {
    return new Promise((resolve, reject) => {
        const workers = [];
        const results = [];
        let completedWorkers = 0;
        
        const simsPerWorker = Math.floor(simCount / NUM_WORKERS);
        const remainder = simCount % NUM_WORKERS;

        for (let i = 0; i < NUM_WORKERS; i++) {
            const workerSimCount = simsPerWorker + (i < remainder ? 1 : 0);
            if (workerSimCount === 0) continue; 

            const worker = new Worker(WORKER_URL);
            workers.push(worker);

            worker.onmessage = function(e) {
                if (e.data.error) {
                    workers.forEach(w => w.terminate()); 
                    return reject(new Error(e.data.error));
                }
                results.push(e.data);
                completedWorkers++;
                worker.terminate(); 

                if (completedWorkers === workers.length) {
                    const finalValues = [];
                    const allPaths = [];
                    const maxDrawdowns = [];

                    results.forEach(res => {
                        finalValues.push(...res.finalValues);
                        allPaths.push(...res.allPaths);
                        maxDrawdowns.push(...res.maxDrawdowns);
                    });

                    const finalResult = analyzeSimulationResults(
                        finalValues, allPaths, maxDrawdowns, simYears, simCount, targetAmount, 
                        initialInvestment, monthlyContribution, contributionIncrease,
                        isWithdrawalMode, minSurvivalYears
                    );
                    resolve(finalResult);
                }
            };
            
            worker.onerror = (err) => {
                workers.forEach(w => w.terminate());
                reject(new Error(`Worker Error: ${err.message}`));
            };

            worker.postMessage({
                returnsBlocks: stats.returnsBlocks, 
                weights: stats.weights,
                simYears,
                initialInvestment,
                simCount: workerSimCount,
                monthlyContribution,
                contributionIncrease,
                isWithdrawalMode,
                withdrawalRate,
                inflationRate
            });
        }
    });
}